import { skipToken } from "@reduxjs/toolkit/query";
import { useGetQuestionnaireSectionsQuery } from "../api/questionnaire";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import { useNotarizationState } from "./useNotarizationState";
import useIsMobile from "./useIsMobile";
import {
  calculateCurrentStep,
  generateProgressLabel,
  TOTAL_STEPS,
} from "../utils/progressTrackerUtils";
import { hasAllRequiredIncomeYears } from "../utils/incomeValidation";
import useCurrentUserPaymentData from "./useCurrentUserPaymentData";
import { useGetIncomesByUserIdQuery } from "../api/financials/incomes";

export interface UseProgressTrackerStateReturn {
  currentStep: number;
  totalSteps: number;
  percentage: number;
  label: string;
  isLoading: boolean;
  error: boolean;
}

export const useProgressTrackerState = (): UseProgressTrackerStateReturn => {
  const isMobile = useIsMobile();
  const { user, document, error: documentError } = useGetCurrentUserDocument();
  const { isPrenupPaid } = useCurrentUserPaymentData();
  const {
    isDocumentReviewed,
    isNotarizationComplete,
    error: notarizationError,
  } = useNotarizationState();

  const { data: questionnaireSections, error: questionnaireError } =
    useGetQuestionnaireSectionsQuery(
      user?.id ? { id: user.id, includeStatus: true } : skipToken,
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const { data: incomes } = useGetIncomesByUserIdQuery(
    user?.id && document?.id
      ? { userId: user.id, docId: document.id }
      : skipToken,
  );

  // Check for errors
  const hasError = Boolean(
    documentError ?? notarizationError ?? questionnaireError,
  );

  // Check if data is still loading
  const isLoading = !questionnaireSections && !hasError;

  // Calculate current step based on completion states
  const hasFinancials = incomes ? incomes.length > 2 : false;

  // Check if income requirements are met (all 3 years present)
  const hasRequiredIncomeYears = hasAllRequiredIncomeYears(incomes ?? []);
  const isFinancialsComplete = hasFinancials && hasRequiredIncomeYears;

  const currentStep = calculateCurrentStep({
    questionnaireSections: questionnaireSections ?? [],
    isPrenupPaid: isPrenupPaid ?? false,
    hasFinancials: isFinancialsComplete,
    isDocumentReviewed,
    isNotarizationComplete,
  });

  const percentage = Math.round((currentStep / TOTAL_STEPS) * 100);

  const label = generateProgressLabel(currentStep, percentage, isMobile);

  return {
    currentStep,
    totalSteps: TOTAL_STEPS,
    percentage,
    label,
    isLoading,
    error: hasError,
  };
};
